/*
 * Actions for the [energy_management_systems store]{link module:store/energy_management_systems}
 *
 * @module store/energy_management_systems/actions
 */

import * as apiEmss from '@/api/energy_management_systems'
import * as apiGroups from '@/api/groups'
import * as apiUsers from '@/api/users'
import { Ems } from '@/store/modules/energy_management_systems'
import { resourceToAmperix } from '@/store/modules/amperixes'

/**
 * Action to reset the EMSs
 *
 * @function
 *
 */
export function resetStore({ commit }) {
  commit('RESET_EMSS')
  return true
}

/**
 * API call to GET all or filtered (by ID) EMSs.
 *
 * @function
 *
 * @param {object} params
 * @param {array} params.emsIds (optional) is an optional filter. A list of EMS's IDs. Will request all if empty
 * @param {string} params.ctxt is the context of the API request.
 * @param {array} params.fields is a list of fields (attributes) to be included in the response
 *
 * @return {promise} which resolves to the list of EMSs (json:api data-response)
 */
export async function getEmss({ commit }, { emsIds = [], ctxt = 'admin', fields = ['name'], filter = {} } = {}) {
  if (emsIds && emsIds.length > 0) {
    filter.id = emsIds.join(',')
  }
  const rsp = await apiEmss.getEnergyManagementSystems({
    ctxt,
    fields,
    filter
  })
  const emss = rsp.payload.data

  for (const ems of emss) {
    const emsParams = {
      id: ems.id,
      ...ems.attributes
    }

    if (ems.relationships?.amperix) {
      if (ems.relationships.amperix?.data?.id) {
        emsParams.serialno = ems.relationships.amperix?.data?.id
      }
    }

    commit('SET_EMS', new Ems(emsParams))
  }

  return emss
}

/**
 * API call to GET one EMS.
 *
 * @function
 *
 * @param {object} params
 * @param {array} params.emsId (required) is the EMS ID.
 * @param {string} params.ctxt is the context of the API request.
 * @param {array} params.fields is a list of fields (attributes) to be included in the response
 * @param {array} params.include is a list of related resources to be included in the response
 *
 * @return {promise} which resolves to the EMS (json:api data-response)
 */
export async function getEms({ commit }, { emsId, ctxt = 'admin', fields = ['name'], include = [] } = {}) {
  const rsp = await apiEmss.getEnergyManagementSystem({
    id: emsId,
    ctxt,
    fields,
    include
  })
  const ems = rsp.payload.data

  const emsParams = {
    id: ems.id
  }

  for (const field of fields) {
    if (field === 'ems_access_group') {
      const emsGroupId = rsp.payload.data.relationships.emsAccessGroup.data.id
      commit('energyManagementSystemGroups/ADD_GROUPS_FOR_EMS', { emsId, emsGroupIds: [emsGroupId] }, { root: true })
    } else {
      emsParams[field] = ems.attributes[field]
    }
  }

  if (ems.relationships?.amperix?.data?.id) {
    emsParams.serialno = ems.relationships.amperix?.data?.id
  }

  commit('SET_EMS', new Ems(emsParams))

  if (rsp.payload.included) {
    for (const includedResource of rsp.payload.included) {
      if (includedResource.type === 'amperixes') {
        commit(
          'amperixes/SET_AMPERIX',
          {
            amperix: resourceToAmperix(includedResource, null)
          },
          {
            root: true
          }
        )
      }
    }
  }

  return ems
}

/**
 * Action to GET the Group (OrganizationGroup) with its EMSs relationships.
 *
 * Adds the EMS IDs of the Group into the store and also fetches (and fills) those EMSs (and the EMS store)
 *
 * @function
 *
 * @param {object} params
 * @param {integer} params.groupId has to be the ID of the observed EmsGroup
 * @param {string} params.ctxt is the context of the API request.
 *
 * @return {promise} which resolves to the Group (json:api data-response)
 */
export async function getEmssForGroup(
  { commit, dispatch },
  { groupId, ctxt = 'admin', withEmsCall = true, includeDescendants = false }
) {
  const rspEms = await apiGroups.getEmsIdsForGroup({ id: groupId, ctxt: ctxt, includeDescendants })

  const emsIds = rspEms.payload.data.map((ems) => {
    return parseInt(ems.id)
  })

  if (withEmsCall) {
    await dispatch('getEmss', { ctxt, emsIds: emsIds })
  }

  commit('SET_EMSS_FOR_GROUP', { groupId, emsIds })

  return emsIds
}

/**
 * Action to GET the User with its EMSs relationships (via `OrganizationGroups` or `EmsAccessGroups`).
 *
 * Only for `admin` context/scope.
 *
 * See also [User API]{@link module:api/users.getUserRelationshipEmss}.
 *
 * Adds the EMS IDs of the User into the store and also fetches (and fills) those EMSs (and the EMS store)
 *
 * @function
 *
 * @param {object} params
 * @param {integer} params.userId has to be the ID of the observed User
 * @param {string} params.membership is the Group type, via the User .-< EMSs relationship is established
 *
 * @return {promise} which resolves to the user-relationships (json:api data-response)
 */
export async function getEmssForUser({ commit, dispatch }, { userId, membership = 'Groups::OrganizationGroup' }) {
  const rsp = await apiUsers.getUserRelationshipEmss({
    id: userId,
    membership
  })
  const emsIds = rsp.payload.data.map((ems) => ems.id)
  commit('SET_EMSS_FOR_USER', { userId, emsIds })

  await dispatch('getEmss', { emsIds })

  return rsp.payload.data
}

/**
 * Action to add the EMSs to a group
 *
 * Starts an API Call to add the EMSs as members to the group and afterwards mirrors the state into the store
 *
 * @function
 *
 * @param {object} params
 * @param {integer[]} params.emsIds are the emss Ids
 * @param {integer} params.groupId
 *
 * @return {promise} which resolves to the Group (json:api data-response)
 */
export async function addEmssToGroup({ commit }, { emsIds = [], groupId = null }) {
  const calls = []
  for (const emsId of emsIds) {
    calls.push(
      apiEmss
        .addEmsToGroup({
          id: emsId,
          groupIds: [groupId]
        })
        .then(() => {
          commit('ADD_EMS_TO_GROUP', { groupId, emsId })
        })
    )
  }

  return Promise.all(calls)
}

/**
 * Action to remove the EMSs from a group
 *
 * Starts an API Call to remove the EMSs as members from the group and afterwards mirrors the state into the store
 *
 * @function
 *
 * @param {object} params
 * @param {integer[]} params.emsIds are the emss Ids
 * @param {integer} params.groupId
 *
 * @return {promise} which resolves to the Group (json:api data-response)
 */
export async function removeEmssFromGroup({ commit }, { emsIds = [], groupId = null }) {
  const calls = []

  for (const emsId of emsIds) {
    calls.push(
      apiEmss
        .removeEmsFromGroup({
          id: emsId,
          groupIds: [groupId]
        })
        .then(() => {
          commit('REMOVE_EMS_FROM_GROUP', { groupId, emsId })
        })
    )
  }

  return Promise.all(calls)
}
